import { useState, forwardRef, useImperativeHandle } from 'react';

/**
 * Icon micro-components
 */
const Ic = ({ size = 15, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

/**
 * BusinessRegistrationForm
 * Reusable form component for admin business registration
 * 
 * Props:
 * - initialData?: {business_name, business_category, website_url, ...}
 * - categories: [{id, name}, ...] - Full category list from API
 * - onSubmit: (formData) => Promise - Called when user clicks Save
 * - onCancel: () => void - Called when user clicks Cancel
 * - isLoading?: boolean - Show loading state on submit button
 * - categoryOptions?: [{id, name}, ...] - Dropdown options (uses categories if not provided)
 */
const BusinessRegistrationForm = forwardRef(function BusinessRegistrationForm({
  initialData = null,
  categories = [],
  onSubmit,
  onCancel,
  isLoading = false,
  categoryOptions = null,
  onFormChange = null,
}, ref) {
  const [form, setForm] = useState(initialData || {
    business_name: '',
    business_category: '',
    website_url: '',
    street_address: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'United States',
    phone_number: '',
    email_business: '',
    business_description: '',
    business_logo_url: '',
    opening_hours: {},
  });

  const [errors, setErrors] = useState({});
  const [slugEdited, setSlugEdited] = useState(!!initialData);

  // Expose submitForm() and reset() to parent via ref
  useImperativeHandle(ref, () => ({
    async submitForm() {
      if (!validateAll()) return;
      try {
        await onSubmit(form);
      } catch (error) {
        console.error('Form submission error:', error);
      }
    },
    reset(data) {
      setForm(data || {
        business_name: '',
        business_category: '',
        website_url: '',
        street_address: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'United States',
        phone_number: '',
        email_business: '',
        business_description: '',
        business_logo_url: '',
        opening_hours: {},
      });
      setErrors({});
      setSlugEdited(!!data);
    },
    isValid() {
      // Check if all required fields are filled (without modifying errors state)
      const requiredFields = [
        'business_name',
        'business_category',
        'website_url',
        'street_address',
        'city',
        'country',
        'phone_number',
        'business_logo_url',
        'business_description',
      ];

      for (const field of requiredFields) {
        if (!form[field] || form[field].toString().trim() === '') {
          return false;
        }
      }

      // Validate URL formats
      if (form.website_url) {
        try {
          new URL(form.website_url);
        } catch {
          return false;
        }
      }

      if (form.business_logo_url) {
        try {
          new URL(form.business_logo_url);
        } catch {
          return false;
        }
      }

      // Validate email (required)
      if (!form.email_business || form.email_business.toString().trim() === '') {
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_business)) {
        return false;
      }

      // Validate phone
      if (form.phone_number && !/[\d\s\-\+\(\)]{10,}/.test(form.phone_number.replace(/\s/g, ''))) {
        return false;
      }

      return true;
    }
  }), [form, onSubmit]);

  const dropdownOptions = categoryOptions || categories;

  /**
   * Validate individual field
   */
  const validateField = (name, value) => {
    const newErrors = { ...errors };
    delete newErrors[name];

    switch (name) {
      case 'business_name':
        if (!value || value.trim().length < 3) {
          newErrors.business_name = 'Business name must be at least 3 characters';
        } else if (value.length > 150) {
          newErrors.business_name = 'Business name must be less than 150 characters';
        }
        break;

      case 'business_category':
        if (!value) {
          newErrors.business_category = 'Please select a category';
        }
        break;

      case 'website_url':
        if (!value) {
          newErrors.website_url = 'Website URL is required';
        } else {
          try {
            new URL(value);
          } catch {
            newErrors.website_url = 'Please enter a valid URL (e.g., https://example.com)';
          }
        }
        break;

      case 'street_address':
        if (!value || value.trim().length < 5) {
          newErrors.street_address = 'Address must be at least 5 characters';
        } else if (value.length > 255) {
          newErrors.street_address = 'Address must be less than 255 characters';
        }
        break;

      case 'city':
        if (!value || value.trim().length < 2) {
          newErrors.city = 'City is required';
        }
        break;

      case 'country':
        if (!value || value.trim().length < 2) {
          newErrors.country = 'Country is required';
        }
        break;

      case 'phone_number':
        if (!value) {
          newErrors.phone_number = 'Phone number is required';
        } else if (!/[\d\s\-\+\(\)]{10,}/.test(value.replace(/\s/g, ''))) {
          newErrors.phone_number = 'Please enter a valid phone number (minimum 10 digits)';
        }
        break;

      case 'email_business':
        if (!value || value.trim().length === 0) {
          newErrors.email_business = 'Business email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email_business = 'Please enter a valid email address';
        }
        break;

      case 'business_description':
        if (!value || value.trim().length === 0) {
          newErrors.business_description = 'Description is required';
        } else if (value.length > 1000) {
          newErrors.business_description = 'Description must be less than 1000 characters';
        }
        break;

      case 'business_logo_url':
        if (!value || value.trim().length === 0) {
          newErrors.business_logo_url = 'Logo URL is required';
        } else {
          try {
            new URL(value);
          } catch {
            newErrors.business_logo_url = 'Please enter a valid URL';
          }
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle field change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    validateField(name, value);
    if (onFormChange) {
      onFormChange();
    }
  };

  /**
   * Validate all fields before submit
   */
  const validateAll = () => {
    const requiredFields = [
      'business_name',
      'business_category',
      'website_url',
      'street_address',
      'city',
      'country',
      'phone_number',
      'email_business',
      'business_logo_url',
      'business_description',
    ];

    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!form[field] || form[field].toString().trim() === '') {
        newErrors[field] = `${field.replace(/_/g, ' ')} is required`;
      }
    });

    // Validate formats
    if (form.website_url) {
      try {
        new URL(form.website_url);
      } catch {
        newErrors.website_url = 'Please enter a valid URL';
      }
    }

    if (
      form.email_business &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_business)
    ) {
      newErrors.email_business = 'Please enter a valid email address';
    }

    if (form.phone_number && !/[\d\s\-\+\(\)]{10,}/.test(form.phone_number.replace(/\s/g, ''))) {
      newErrors.phone_number = 'Please enter a valid phone number';
    }

    if (form.business_logo_url) {
      try {
        new URL(form.business_logo_url);
      } catch {
        newErrors.business_logo_url = 'Please enter a valid URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAll()) {
      return;
    }

    try {
      await onSubmit(form);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const getFieldError = (fieldName) => errors[fieldName];
  const hasError = (fieldName) => !!errors[fieldName];

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      {/* ── Section 1: Basic Information ── */}
      <div className="space-y-5 pb-5 border-b border-slate-200">
        <div>
          <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
            Business Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="business_name"
            required
            value={form.business_name}
            onChange={handleChange}
            placeholder="e.g., Taj Hotels Pvt Ltd"
            className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-1 transition-colors
              ${hasError('business_name')
                ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
          />
          {hasError('business_name') && (
            <p className="text-[11px] text-red-600 mt-1">{getFieldError('business_name')}</p>
          )}
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
            Business Category <span className="text-red-500">*</span>
          </label>
          <select
            name="business_category"
            required
            value={form.business_category}
            onChange={handleChange}
            className={`w-full px-3 py-2 text-[13px] text-slate-800 border rounded-lg focus:outline-none focus:ring-1 transition-colors
              ${hasError('business_category')
                ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
          >
            <option value="">Select a category...</option>
            {dropdownOptions.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          {hasError('business_category') && (
            <p className="text-[11px] text-red-600 mt-1">{getFieldError('business_category')}</p>
          )}
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
            Website URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            name="website_url"
            required
            value={form.website_url}
            onChange={handleChange}
            placeholder="https://example.in"
            className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-1 transition-colors
              ${hasError('website_url')
                ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
          />
          {hasError('website_url') && (
            <p className="text-[11px] text-red-600 mt-1">{getFieldError('website_url')}</p>
          )}
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="phone_number"
            required
            value={form.phone_number}
            onChange={handleChange}
            placeholder="+91 (98765) 43210"
            className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-1 transition-colors
              ${hasError('phone_number')
                ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
          />
          {hasError('phone_number') && (
            <p className="text-[11px] text-red-600 mt-1">{getFieldError('phone_number')}</p>
          )}
        </div>
      </div>

      {/* ── Section 2: Location & Contact ── */}
      <div className="space-y-5 py-5 border-b border-slate-200">
        <div>
          <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
            Street Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="street_address"
            required
            value={form.street_address}
            onChange={handleChange}
            placeholder="Plot 123, MG Road, Sector 01"
            className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-1 transition-colors
              ${hasError('street_address')
                ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
          />
          {hasError('street_address') && (
            <p className="text-[11px] text-red-600 mt-1">{getFieldError('street_address')}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="city"
              required
              value={form.city}
              onChange={handleChange}
              placeholder="Mumbai"
              className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-1 transition-colors
                ${hasError('city')
                  ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                  : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
                }`}
            />
            {hasError('city') && (
              <p className="text-[11px] text-red-600 mt-1">{getFieldError('city')}</p>
            )}
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
              State/Province
            </label>
            <input
              type="text"
              name="state_province"
              value={form.state_province}
              onChange={handleChange}
              placeholder="Maharashtra"
              className="w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
              Postal Code
            </label>
            <input
              type="text"
              name="postal_code"
              value={form.postal_code}
              onChange={handleChange}
              placeholder="400001"
              className="w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
              Country <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="country"
              required
              value={form.country}
              onChange={handleChange}
              placeholder="India"
              className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-1 transition-colors
                ${hasError('country')
                  ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                  : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
                }`}
            />
            {hasError('country') && (
              <p className="text-[11px] text-red-600 mt-1">{getFieldError('country')}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
            Business Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email_business"
            required
            value={form.email_business}
            onChange={handleChange}
            placeholder="contact@business.in"
            className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-1 transition-colors
              ${hasError('email_business')
                ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
          />
          {hasError('email_business') && (
            <p className="text-[11px] text-red-600 mt-1">{getFieldError('email_business')}</p>
          )}
        </div>
      </div>

      {/* ── Section 3: Logo & Description ── */}
      <div className="space-y-5 py-5 border-b border-slate-200">
        <div>
          <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
            Logo URL <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="url"
                name="business_logo_url"
                required
                value={form.business_logo_url}
                onChange={handleChange}
                placeholder="https://example.in/logo.png"
                className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-1 transition-colors
                  ${hasError('business_logo_url')
                    ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                    : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
                  }`}
              />
            </div>
            {form.business_logo_url && (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 overflow-hidden">
                <img src={form.business_logo_url} alt="Logo preview" className="h-full w-full object-cover" />
              </div>
            )}
          </div>
          {hasError('business_logo_url') && (
            <p className="text-[11px] text-red-600 mt-1">{getFieldError('business_logo_url')}</p>
          )}
          <p className="text-[11px] text-slate-400 mt-1">Direct image URL (JPG/PNG, recommended 200x200px)</p>
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="business_description"
            required
            value={form.business_description}
            onChange={handleChange}
            rows={3}
            placeholder="Brief description of your business..."
            className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg resize-none focus:outline-none focus:ring-1 transition-colors
              ${hasError('business_description')
                ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
          />
          {hasError('business_description') && (
            <p className="text-[11px] text-red-600 mt-1">{getFieldError('business_description')}</p>
          )}
          <div className="flex items-center justify-between mt-1">
            <p className="text-[11px] text-slate-400">Max 1000 characters</p>
            <p className="text-[11px] font-medium text-slate-500">
              {form.business_description.length}/1000
            </p>
          </div>
        </div>
      </div>

      {/* ── Section 4: Notes ── */}
      <div className="py-5 bg-blue-50 border-l-2 border-blue-400 pl-4 mb-6">
        <p className="text-[12px] text-blue-900 font-medium">Auto-Set Fields</p>
        <ul className="text-[11px] text-blue-800 space-y-0.5 mt-2 list-disc list-inside">
          <li>Verification Status → Verified</li>
          <li>Published Status → Published</li>
          <li>Created By → Admin</li>
        </ul>
      </div>

      {/* Return form data for parent component to handle submission */}
      {/* This is intentionally left here - the actual submission is handled via onSubmit prop */}
    </form>
  );
});

export default BusinessRegistrationForm;
